import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react'
import { Formik, Form } from 'formik'
import classNames from 'classnames'
import { Essentials } from '@ckeditor/ckeditor5-essentials'
import { Paragraph } from '@ckeditor/ckeditor5-paragraph'
import { ListProperties } from '@ckeditor/ckeditor5-list'
import {
  useErrorService,
  useNotificationService,
  ABSpinner,
} from '@abroad/components'
import { ExperienceInputField } from '../form'
import SignupPreviewModal from '../OrgEco/SignupPreviewModal'
import OnboardingExampleModal from './OnboardingExampleModal'
import ExperienceCKEditor from './ExperienceCKEditor'
import API from '../../utils/API'
import { queueAutosaveOnBlur, FormikDirtyReporter } from './questAutosave'
import {
  SIGNUP_LIMITS,
  validateSignupPageValues,
  visibleCharCount,
} from './enrollmentValidation'

const signupHeaderPlaceholder = 'Your Journey Begins with Awareness'
const signupTitlePlaceholder = 'True Prosperity in Bhutan'

const resolveNetworkId = (networkId) => {
  if (!networkId) return ''
  if (typeof networkId === 'string') return networkId
  if (typeof networkId === 'object') {
    return networkId._id || networkId.id || ''
  }
  return String(networkId)
}

const CharCount = ({ current, max }) => {
  const over = current > max
  return (
    <div className='experience-field-foot'>
      <span
        className={classNames('experience-char-count', {
          error: over,
        })}>
        {current} / {max}
      </span>
    </div>
  )
}

const CustomiseOnboarding = forwardRef(
  ({ type, networkId, onCompletenessChange, onSaveStateChange }, ref) => {
    const resolvedNetworkId = resolveNetworkId(networkId)
    const [initialValues, setInitialValues] = useState({
      header: '',
      body: '',
      title: '',
      welcomeLetter: '',
    })
    const [isLoading, setIsLoading] = useState(true)
    const [isEditing, setIsEditing] = useState(false)
    const [showPreview, setShowPreview] = useState(false)
    const [exampleKey, setExampleKey] = useState(null)
    const valuesRef = useRef(initialValues)
    const Error = useErrorService()
    const Notification = useNotificationService()

    const reportCompleteness = (values) => {
      if (typeof onCompletenessChange !== 'function') return
      onCompletenessChange({
        welcomeLetter: values?.welcomeLetter || '',
        signupHeader: values?.header || '',
        signupBody: values?.body || '',
        signupTitle: values?.title || '',
        signupPageContent: values?.body || '',
      })
    }

    useImperativeHandle(ref, () => ({
      openPreview: () => setShowPreview(true),
    }))

    useEffect(() => {
      const fetchInviteDetails = async () => {
        if (type && resolvedNetworkId) {
          setIsLoading(true)
          try {
            const { data } =
              await API.AdminAPI.entityService.getEntityInviteLinkInfo(
                type,
                resolvedNetworkId,
              )
            if (data) {
              const hasData =
                data.header || data.body || data.title || data.welcomeLetter
              setIsEditing(hasData)
              const nextValues = {
                header: data.header || '',
                body: data.body || '',
                title: data.title || '',
                welcomeLetter: data.welcomeLetter || '',
              }
              valuesRef.current = nextValues
              setInitialValues(nextValues)
              reportCompleteness(nextValues)
            }
          } catch (e) {
            Error.showError(e)
          } finally {
            setIsLoading(false)
          }
        } else {
          setIsLoading(false)
          Notification.showNotification(
            'This experience is not associated with any network.',
            'danger',
          )
        }
      }

      fetchInviteDetails()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, resolvedNetworkId])

    const toPayload = (values) => ({
      header: (values.header || '').trim() || null,
      body: values.body || null,
      title: (values.title || '').trim() || null,
      welcomeLetter: values.welcomeLetter || null,
    })

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
      if (!type || !resolvedNetworkId) {
        setSubmitting(false)
        return
      }
      const errors = validateSignupPageValues(values)
      if (Object.keys(errors).length > 0) {
        reportCompleteness(values)
        setSubmitting(false)
        return
      }
      setSubmitting(true)
      if (typeof onSaveStateChange === 'function') {
        onSaveStateChange('saving')
      }
      try {
        await API.AdminAPI.entityService.updateEntityInviteDetails(
          type,
          resolvedNetworkId,
          toPayload(values),
        )
        // Keep any edits typed while the request was in flight.
        const latest = valuesRef.current
        valuesRef.current = latest
        setInitialValues(latest)
        resetForm({ values: latest })
        reportCompleteness(latest)
        if (!isEditing) {
          setIsEditing(true)
        }
        if (typeof onSaveStateChange === 'function') {
          onSaveStateChange('saved')
        }
        // If the user changed fields during save, persist the newer snapshot.
        const savedPayload = JSON.stringify(toPayload(values))
        const latestPayload = JSON.stringify(toPayload(latest))
        if (
          savedPayload !== latestPayload &&
          Object.keys(validateSignupPageValues(latest)).length === 0
        ) {
          await API.AdminAPI.entityService.updateEntityInviteDetails(
            type,
            resolvedNetworkId,
            toPayload(latest),
          )
          setInitialValues(latest)
          resetForm({ values: latest })
          reportCompleteness(latest)
        }
      } catch (e) {
        Error.showError(e)
        if (typeof onSaveStateChange === 'function') {
          onSaveStateChange('error')
        }
      } finally {
        setSubmitting(false)
      }
    }

    const signupBodyEditorConfig = {
      licenseKey: 'GPL',
      plugins: [Essentials, Paragraph, ListProperties],
      toolbar: ['bulletedList', 'numberedList'],
    }

    if (isLoading) {
      return <ABSpinner />
    }

    return (
      <Formik
        initialValues={initialValues}
        validate={validateSignupPageValues}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnBlur
        validateOnChange={false}>
        {(formik) => {
          const {
            values,
            setFieldValue,
            errors,
            touched,
            setFieldTouched,
            dirty,
          } = formik
          valuesRef.current = values
          const syncCompleteness = (nextValues) => {
            valuesRef.current = nextValues
            reportCompleteness(nextValues)
          }
          const fieldBlur = async (e) => {
            if (e?.target?.name) {
              await setFieldTouched(e.target.name, true, true)
            }
            syncCompleteness(valuesRef.current)
            queueAutosaveOnBlur(formik)
          }
          const editorBlur = (fieldName) => (event, editor) => {
            const data = editor.getData()
            setFieldValue(fieldName, data, false)
            setFieldTouched(fieldName, true, true)
            window.setTimeout(() => {
              reportCompleteness(valuesRef.current)
              queueAutosaveOnBlur(formik)
            }, 0)
          }

          return (
            <Form>
              <FormikDirtyReporter id='enrol-signup' dirty={dirty} />
              <fieldset className='experience-section-fieldset'>
                <SignupPreviewModal
                  show={showPreview}
                  onHide={() => setShowPreview(false)}
                  orgEcoId={resolvedNetworkId}
                  signupTitle={signupHeaderPlaceholder}
                  signupSubTitle={signupTitlePlaceholder}
                />

                {resolvedNetworkId ? (
                  <div className='experience-example-banners'>
                    {[
                      {
                        key: 'letter',
                        label: 'Welcome letter',
                        hint: 'The first screen invited guests see.',
                      },
                      {
                        key: 'signup',
                        label: 'Signup page',
                        hint: 'Where they create their account.',
                        mockClass: 'signup',
                      },
                    ].map(({ key, label, hint, mockClass }) => (
                      <button
                        key={key}
                        type='button'
                        className='experience-example-banner'
                        onClick={() => setExampleKey(key)}>
                        <span
                          className={classNames(
                            'experience-onboard-mock',
                            mockClass,
                          )}>
                          <span className='experience-form-card' />
                        </span>
                        <span className='experience-example-copy'>
                          <b>{label}</b>
                          <small>{hint}</small>
                        </span>
                        <span className='experience-example-see'>
                          See example →
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}

                <OnboardingExampleModal
                  show={Boolean(exampleKey)}
                  exampleKey={exampleKey}
                  onHide={() => setExampleKey(null)}
                />

                <div className='experience-enrol-block'>
                  <div className='experience-field-head'>
                    <h4>Welcome letter</h4>
                  </div>
                  <div className='experience-f mb-3'>
                    <ExperienceInputField
                      name='title'
                      label='Welcome letter title'
                      placeholder={signupTitlePlaceholder}
                      capitalizeFirstLetter
                      showErrorMessage
                      displayFlex={false}
                      onChange={(e) => {
                        setFieldValue('title', e.target.value, false)
                        syncCompleteness({
                          ...valuesRef.current,
                          title: e.target.value,
                        })
                      }}
                      onBlur={fieldBlur}
                    />
                    <CharCount
                      current={visibleCharCount(values.title)}
                      max={SIGNUP_LIMITS.title}
                    />
                  </div>
                  <div className='experience-f mb-3'>
                    <label className='experience-f-label'>
                      Welcome letter description
                    </label>
                    <p className='experience-f-hint mb-2 text-dark'>
                      Salutation (Hi Name) is added automatically. Do not add it
                      below.
                    </p>
                    <ExperienceCKEditor
                      name='welcomeLetter'
                      data={values.welcomeLetter || ''}
                      maxLength={SIGNUP_LIMITS.welcomeLetter}
                      className={
                        touched.welcomeLetter && errors.welcomeLetter
                          ? 'has-error'
                          : undefined
                      }
                      onReady={(editor) => {
                        if (editor) {
                          editor.editing.view.change((writer) => {
                            writer.setStyle(
                              'min-height',
                              '168px',
                              editor.editing.view.document.getRoot(),
                            )
                          })
                        }
                      }}
                      onChange={(event, editor) => {
                        const data = editor.getData()
                        setFieldValue('welcomeLetter', data, false)
                        syncCompleteness({
                          ...valuesRef.current,
                          welcomeLetter: data,
                        })
                      }}
                      onBlur={editorBlur('welcomeLetter')}
                    />
                    {touched.welcomeLetter && errors.welcomeLetter ? (
                      <p className='experience-f-error mt-2 mb-0' role='alert'>
                        {errors.welcomeLetter}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className='experience-enrol-block'>
                  <div className='experience-field-head'>
                    <h4>Signup page</h4>
                  </div>
                  <div className='experience-f mb-3'>
                    <ExperienceInputField
                      name='header'
                      label='Signup page header'
                      placeholder={signupHeaderPlaceholder}
                      showErrorMessage
                      displayFlex={false}
                      capitalizeFirstLetter
                      onChange={(e) => {
                        setFieldValue('header', e.target.value, false)
                        syncCompleteness({
                          ...valuesRef.current,
                          header: e.target.value,
                        })
                      }}
                      onBlur={fieldBlur}
                    />
                    <CharCount
                      current={visibleCharCount(values.header)}
                      max={SIGNUP_LIMITS.header}
                    />
                  </div>
                  <div className='experience-f mb-3'>
                    <label className='experience-f-label'>
                      Signup page description
                    </label>
                    <ExperienceCKEditor
                      name='body'
                      data={values.body || ''}
                      config={signupBodyEditorConfig}
                      maxLength={SIGNUP_LIMITS.body}
                      className={
                        touched.body && errors.body ? 'has-error' : undefined
                      }
                      onReady={(editor) => {
                        if (editor) {
                          editor.editing.view.change((writer) => {
                            writer.setStyle(
                              'min-height',
                              '120px',
                              editor.editing.view.document.getRoot(),
                            )
                          })
                        }
                      }}
                      onChange={(event, editor) => {
                        const data = editor.getData()
                        setFieldValue('body', data, false)
                        syncCompleteness({ ...valuesRef.current, body: data })
                      }}
                      onBlur={editorBlur('body')}
                    />
                    <p className='experience-f-hint text-dark'>
                      Simple formatting only — lists. Maximum{' '}
                      {SIGNUP_LIMITS.body} characters.
                    </p>
                    {touched.body && errors.body ? (
                      <p className='experience-f-error mt-1 mb-0' role='alert'>
                        {errors.body}
                      </p>
                    ) : null}
                  </div>
                </div>

                <button type='submit' hidden aria-hidden />
              </fieldset>
            </Form>
          )
        }}
      </Formik>
    )
  },
)

CustomiseOnboarding.displayName = 'CustomiseOnboarding'

export default CustomiseOnboarding
